import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Pagination } from '@/components/Pagination';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SearchInput } from '@/components/SearchInput';
import {
  defaultTemplatesService,
  DefaultTemplate,
  TemplateCategory,
} from '@/services/default-templates.service';
import { Pencil, Trash2, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';

const CATEGORIES: { label: string; value: TemplateCategory | '' }[] = [
  { label: 'All', value: '' },
  { label: 'Marketing', value: 'marketing' },
  { label: 'Utility', value: 'utility' },
  { label: 'Authentication', value: 'authentication' },
];

const CATEGORY_COLORS: Record<TemplateCategory, string> = {
  marketing: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  utility: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  authentication: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

export default function DefaultTemplatesList() {
  const nav = useNavigate();
  const [data, setData] = useState<{ items: DefaultTemplate[]; total: number; page: number; limit: number } | null>(null);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState<TemplateCategory | ''>('');
  const [page, setPage] = useState(1);
  const [del, setDel] = useState<DefaultTemplate | null>(null);

  const refresh = useCallback(() => {
    defaultTemplatesService
      .list({ q: q || undefined, category: category || undefined, page, limit: 12 })
      .then(setData)
      .catch(() => undefined);
  }, [q, category, page]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleDelete = async () => {
    if (!del) return;
    await defaultTemplatesService.remove(del.defaultTemplateUid);
    toast.success('Deleted');
    setDel(null);
    refresh();
  };

  return (
    <>
      <Topbar
        title="Default Templates"
        breadcrumb="Catalog / Default Templates"
        right={
          <Button onClick={() => nav('/default-templates/new')}>
            + New default template
          </Button>
        }
      />
      <div className="p-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <SearchInput
            value={q}
            onChange={(v) => { setQ(v); setPage(1); }}
            placeholder="Search templates…"
          />
          <div className="flex gap-1.5">
            {CATEGORIES.map((c) => (
              <button
                key={c.value}
                onClick={() => { setCategory(c.value); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  category === c.value
                    ? 'bg-primary text-on-primary'
                    : 'bg-surface border border-border text-text-primary hover:bg-surface-alt'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {!data ? (
          /* Skeleton */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl border border-border bg-surface shadow-soft overflow-hidden animate-pulse">
                <div className="h-40 bg-surface-alt" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-surface-alt rounded w-3/4" />
                  <div className="h-3 bg-surface-alt rounded w-full" />
                  <div className="h-3 bg-surface-alt rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        ) : data.items.length === 0 ? (
          /* Empty state */
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10 mb-5">
              <Sparkles size={36} className="text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-text-primary mb-1">No templates yet</h3>
            <p className="text-sm text-text-muted mb-5 max-w-xs">
              Create premium starting-point templates that every company can use in their gallery.
            </p>
            <Button onClick={() => nav('/default-templates/new')}>+ New default template</Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.items.map((tpl) => (
                <TemplateCard
                  key={tpl.defaultTemplateUid}
                  tpl={tpl}
                  onEdit={() => nav(`/default-templates/${tpl.defaultTemplateUid}`)}
                  onDelete={() => setDel(tpl)}
                />
              ))}
            </div>
            <div className="mt-6">
              <Pagination page={data.page} total={data.total} limit={data.limit} onChange={setPage} />
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={!!del}
        title="Delete default template?"
        message={`Delete "${del?.title}"? Companies who have already cloned it will keep their copies.`}
        danger
        onConfirm={handleDelete}
        onCancel={() => setDel(null)}
      />
    </>
  );
}

function TemplateCard({
  tpl,
  onEdit,
  onDelete,
}: {
  tpl: DefaultTemplate;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="group rounded-2xl border border-border bg-surface shadow-soft overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-primary/40">
      {/* Thumbnail */}
      {tpl.thumbnailUrl ? (
        <div className="relative overflow-hidden" style={{ aspectRatio: '16/9' }}>
          <img
            src={tpl.thumbnailUrl}
            alt={tpl.title}
            className="w-full h-full object-cover"
            onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
          />
          {/* Gold shimmer overlay on hover */}
          <div className="absolute inset-0 bg-gradient-to-br from-amber-400/0 to-amber-600/0 group-hover:from-amber-400/10 group-hover:to-amber-600/10 transition-all duration-300" />
        </div>
      ) : (
        <div className="flex items-center justify-center bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20" style={{ aspectRatio: '16/9' }}>
          <Sparkles size={32} className="text-amber-500 opacity-60" />
        </div>
      )}

      {/* Body */}
      <div className="p-4">
        <h4 className="font-semibold text-text-primary text-sm leading-snug mb-1 line-clamp-1">{tpl.title}</h4>
        {tpl.description && (
          <p className="text-xs text-text-muted line-clamp-2 mb-3 leading-relaxed">{tpl.description}</p>
        )}
        {tpl.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {tpl.tags.slice(0, 4).map((tag) => (
              <span key={tag} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface-alt border border-border text-text-muted">
                {tag}
              </span>
            ))}
            {tpl.tags.length > 4 && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-surface-alt border border-border text-text-muted">
                +{tpl.tags.length - 4}
              </span>
            )}
          </div>
        )}

        {/* Footer row */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${CATEGORY_COLORS[tpl.category]}`}>
              {tpl.category}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
              tpl.isActive
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-surface-alt text-text-muted border border-border'
            }`}>
              {tpl.isActive ? 'Active' : 'Hidden'}
            </span>
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={onEdit}
              className="p-1.5 rounded-md text-text-muted hover:text-primary hover:bg-primary/10 transition-colors"
              aria-label="Edit"
            >
              <Pencil size={13} />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 rounded-md text-text-muted hover:text-danger hover:bg-danger/10 transition-colors"
              aria-label="Delete"
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
